import { ContainerItem } from '../../../models/logs/ContainerItem';
import { ContainerRow, normalizeItemType, toContainerItems } from './container-items.rules';

/**
 * container-plan.rules.ts
 *
 * Turns "what the delivery should now hold" — the counts the user leaves the
 * container chips on — into the two things the edit endpoint actually takes:
 * `containers.added` and `containers.voidItemIds`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS ISN'T JUST ARITHMETIC
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * On the mobile new-log flow, a container count is a number: tap Box three
 * times, three labels print. On an EDIT it is not, because every one of those
 * labels already exists in the world with an `itemId` printed in a QR on the
 * side of a physical box.
 *
 * So tapping the count UP is genuinely just a number — the backend mints new
 * item rows and their ids. Tapping it DOWN is not: it means one of those
 * specific boxes is being taken off the log, and the row is TOMBSTONED (never
 * deleted) so the id in the QR still resolves. Which one is a real question with
 * a real answer, and the code cannot invent it.
 *
 * What this module does is pick the least surprising answer — the HIGHEST-numbered
 * items of that type, i.e. take BOX 3 off a log of three, not BOX 1.
 *
 * The edit screen does NOT offer that choice back to the user: there is no
 * per-container picker, deliberately (see `onContainerRowsChange` there). Every void
 * renumbers the survivors of its type and forces a reprint, and hand-applying fresh
 * stickers does not preserve which id was on which box — so picking a specific
 * container buys an accuracy the next reprint discards. The screen states the COUNT
 * coming off and whether survivors are renumbered; these ids are what goes on the
 * wire, not a selection the user edits.
 */

/** One active item row, narrowed to what a void proposal needs. */
export interface PlannableItem {
  itemId: string;
  itemType: string | null;
  /** Position within its type. Used only to void from the top down. */
  typeIndex: number;
}

/** What the chip counts imply for the `containers` section of the request. */
export interface ContainerPlan {
  /** New containers to mint, as `(quantity, itemType)` pairs. Never carries an `itemId` (§3.6). */
  added: ContainerItem[];
  /**
   * The item ids the counts imply should be voided, highest `typeIndex` first.
   *
   * Named `proposed` because it is this module's CHOICE of which containers the
   * counts mean, not because the screen offers it for editing — it does not (see the
   * file header). These ids go straight onto `containers.voidItemIds`.
   */
  proposedVoidItemIds: string[];
}

/** How many ACTIVE items of each normalized type are on the log right now. */
export function currentCountsByType(activeItems: PlannableItem[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of activeItems ?? []) {
    const type = normalizeItemType(item?.itemType);
    if (!type) { continue; }
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }
  return counts;
}

/** The chip rows to seed the editor with: the log's active containers, as they stand. */
export function containerRowsFromItems(activeItems: PlannableItem[]): ContainerRow[] {
  return [...currentCountsByType(activeItems).entries()]
    .map(([itemType, quantity]) => ({ quantity, itemType }));
}

/**
 * Compares the target counts against the active item rows and proposes the
 * additions and voids that would get from one to the other.
 *
 * @param activeItems The log's ACTIVE item rows (voided ones are already gone).
 * @param targetRows What the chips are now set to.
 */
export function planContainerChanges(
  activeItems: PlannableItem[],
  targetRows: ContainerRow[],
): ContainerPlan {
  const current = currentCountsByType(activeItems ?? []);
  const target = new Map<string, number>();
  for (const item of toContainerItems(targetRows ?? [])) {
    target.set(item.itemType, (target.get(item.itemType) ?? 0) + item.quantity);
  }

  const added: ContainerItem[] = [];
  const proposedVoidItemIds: string[] = [];

  // Every type the user is asking for, in the order the chips produced them, so
  // the additions read in the order the user built them.
  for (const [type, wanted] of target) {
    const have = current.get(type) ?? 0;
    if (wanted > have) {
      added.push({ quantity: wanted - have, itemType: type });
    } else if (wanted < have) {
      proposedVoidItemIds.push(...voidCandidates(activeItems ?? [], type, have - wanted));
    }
  }

  // A type dropped to zero disappears from the chips entirely rather than
  // arriving as a 0 — it still means "void all of these".
  for (const [type, have] of current) {
    if (!target.has(type)) {
      proposedVoidItemIds.push(...voidCandidates(activeItems ?? [], type, have));
    }
  }

  return { added, proposedVoidItemIds };
}

/**
 * The `count` items of a type to propose voiding: the highest `typeIndex` first.
 *
 * Taking BOX 3 off a log of three is the one choice that needs no explanation.
 * Taking BOX 1 off would be equally valid to the backend and would read, to
 * anyone holding the boxes, like the wrong one was picked.
 */
function voidCandidates(activeItems: PlannableItem[], type: string, count: number): string[] {
  return activeItems
    .filter((item) => normalizeItemType(item?.itemType) === type)
    .sort((a, b) => (b?.typeIndex ?? 0) - (a?.typeIndex ?? 0))
    .slice(0, Math.max(0, count))
    .map((item) => item.itemId)
    .filter((id) => !!id);
}

/**
 * Whether an ADDITION lands on a type the log already has.
 *
 * The distinction is the whole point of §3.4's warning and is easy to get wrong:
 * adding a box to a log that already has two turns `BOX 1 of 2` into `BOX 1 of 3`
 * and every existing box label is now wrong. Adding a CRATE to a log that has no
 * crates renumbers nothing — the new labels are the only crate labels there are.
 * Warning about both trains people to skip the warning.
 */
export function addsToExistingType(activeItems: PlannableItem[], plan: ContainerPlan): boolean {
  const current = currentCountsByType(activeItems ?? []);
  return (plan?.added ?? []).some((item) => (current.get(normalizeItemType(item.itemType)) ?? 0) > 0);
}

/**
 * Whether this plan will actually move any EXISTING label's caption.
 *
 * Two ways it can, and only two:
 *
 * - an addition onto a type that already has items (see {@link addsToExistingType});
 * - a void that leaves at least one survivor of its type, which is renumbered to
 *   close the gap. Voiding every item of a type renumbers nothing — there is
 *   nothing of that type left to renumber.
 *
 * The authoritative list of what moved is always `renumberedItems` on the
 * response. This is only the gate on the pre-save confirmation, and it exists so
 * that confirmation is not shown for edits that cannot invalidate a label.
 */
export function planRenumbersLabels(activeItems: PlannableItem[], plan: ContainerPlan): boolean {
  if (addsToExistingType(activeItems, plan)) { return true; }

  const voided = new Set(plan?.proposedVoidItemIds ?? []);
  if (voided.size === 0) { return false; }

  const survivorsByType = currentCountsByType(
    (activeItems ?? []).filter((item) => !voided.has(item.itemId)),
  );
  // Any type losing an item but keeping at least one has its survivors renumbered.
  return (activeItems ?? []).some((item) =>
    voided.has(item.itemId) && (survivorsByType.get(normalizeItemType(item.itemType)) ?? 0) > 0);
}
